// Generatore di incontri per gara mista a squadre
// Regole: tutte le squadre si affrontano tra loro, per ogni categoria selezionata
// Ogni match ha: squadraA, squadraB, categoria, stoccate, punteggi, stato, note

/**
 * Genera tutti gli incontri tra squadre per ogni categoria.
 * Se alternate=true, alterna i match tra categorie (round robin alternato tra categorie).
 * Se alternate=false, tutti i match di una categoria, poi l'altra (comportamento classico).
 * @param {Object} params
 * @param {Array} params.teams - Array di squadre
 * @param {Array} params.categories - Array di categorie
 * @param {number} params.stoccate - Stoccate per match
 * @param {boolean} [params.alternate=true] - Se true alterna i match tra categorie
 * @returns {Array} Array di match
 */
import { generateZigZagBottomUpSequence } from './sequenceGenerator';
import { fillNamesFromBottom } from './fencingLogic';

/**
 * Genera tutti gli assalti per una gara a squadre mista, con logica flessibile "dal basso" e alternanza categorie.
 * @param {Object} params
 * @param {Array} params.teams - Array di squadre (ciascuna con membri ordinati per categoria)
 * @param {Array} params.categories - Array di categorie
 * @param {number} params.stoccate - Stoccate per assalto
 * @param {boolean} [params.alternate=true] - Se true alterna le categorie, altrimenti le raggruppa
 * @returns {Array} Array di match/assalti
 */
export function generateMixedMatches({ teams, categories, stoccate, alternate = true }) {
  if (!Array.isArray(teams) || teams.length < 2 || !Array.isArray(categories) || categories.length === 0) return [];

  // Ogni coppia di squadre si affronta
  let allMatches = [];
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      const squadraA = teams[i];
      const squadraB = teams[j];
      // Per ogni categoria, genera la sequenza "dal basso"
      const assaltiByCat = categories.map(cat => {
        const membriA = (squadraA.members || []).filter(m => m.category === cat);
        const membriB = (squadraB.members || []).filter(m => m.category === cat);
        const sequence = generateZigZagBottomUpSequence(membriA.length, membriB.length);
        // Assegna i nomi rispettando l'ordine e la logica "dal basso"
        const redOrder = fillNamesFromBottom(sequence, membriA.map(m => m.name), 'red');
        const greenOrder = fillNamesFromBottom(sequence, membriB.map(m => m.name), 'green');
        return sequence.map((pair, idx) => ({
          fencerA: redOrder[idx],
          fencerB: greenOrder[idx],
          squadraA: squadraA.name,
          squadraB: squadraB.name,
          categoria: cat,
          stoccate,
          stato: 'da disputare',
          note: '',
          id: `${i}_${j}_${cat}_${idx}`
        }));
      });
      // Alternanza categorie (zig-zag) oppure raggruppamento
      let assalti;
      if (alternate) {
        const maxLen = Math.max(...assaltiByCat.map(arr => arr.length));
        assalti = [];
        for (let idx = 0; idx < maxLen; idx++) {
          for (let catIdx = 0; catIdx < assaltiByCat.length; catIdx++) {
            if (assaltiByCat[catIdx][idx]) {
              assalti.push(assaltiByCat[catIdx][idx]);
            }
          }
        }
      } else {
        assalti = assaltiByCat.flat();
      }
      allMatches.push({
        squadraA: squadraA.name,
        squadraB: squadraB.name,
        categorie: categories,
        stoccate,
        assalti,
        id: `${i}_${j}_${categories.join('_')}`
      });
    }
  }
  return allMatches;
}


// Merge intelligente: preserva i risultati già inseriti se squadre/categorie cambiano
export function mergeMixedMatches(oldMatches, newMatches) {
  return newMatches.map(m => {
    // Cerca match esistente con stesse squadre (usa entrambe le convenzioni di naming)
    const found = oldMatches.find(o => {
      // Match by id first (most reliable)
      if (o.id && m.id && o.id === m.id) return true;

      // Match by squadra names (per mixed mode)
      const squadraAMatch = (o.squadraA === m.squadraA) || (o.teamA === m.squadraA) || (o.squadraA === m.teamA);
      const squadraBMatch = (o.squadraB === m.squadraB) || (o.teamB === m.squadraB) || (o.squadraB === m.teamB);

      // Controlla anche l'ordine inverso (A vs B = B vs A)
      const squadraAMatchReverse = (o.squadraA === m.squadraB) || (o.teamA === m.squadraB) || (o.squadraA === m.teamB);
      const squadraBMatchReverse = (o.squadraB === m.squadraA) || (o.teamB === m.squadraA) || (o.squadraB === m.teamA);

      return (squadraAMatch && squadraBMatch) || (squadraAMatchReverse && squadraBMatchReverse);
    });
    return found ? { ...m, ...pickResultFields(found) } : m;
  });
}

function pickResultFields(match) {
  // Tutti i campi risultato da preservare durante il merge
  return {
    punteggioA: match.punteggioA,
    punteggioB: match.punteggioB,
    scoreA: match.scoreA,
    scoreB: match.scoreB,
    stato: match.stato,
    completed: match.completed,
    note: match.note
  };
}
